import { api, ApiError } from './api';
import {
  ProviderSettlement,
  ProviderSettlementDetail,
  ProviderSettlementStatus,
  ProviderTrip,
  PayPeriodInput,
  RecordProviderPaymentInput,
  TripPeriod,
  UnsettledProvider,
  UnsettledProviderTrips,
} from '../types/providerSettlements';

/**
 * Spec 053 Flow F7 — provider settlements, served by **merchant-settlement** under
 * `/api/admin/provider-settlements/**` (gateway route `settlement-admin-provider-settlements`,
 * screen `settlements`; delta §9).
 *
 * Endpoint set is delta D32, which supersedes design.md:
 *   POST   /prepare?providerId=          201 detail · 409 "Nothing to settle for this provider"
 *   GET    ?providerId=&status=          bare list of settlements
 *   GET    /{id}                         { settlement, trips, paymentProofUrl }
 *   GET    /unsettled-providers          #634 — every provider still owed, with its trip count
 *   GET    /unsettled-trips?providerId=[&from=&to=]  #634 — the pool, optionally within a period
 *   POST   /pay-period                   multipart — mark a period's trips paid in one step (2026-09-27)
 *   POST   /{id}/invoice                 multipart invoiceFile — attach the invoice after payment
 *   GET    /order-trips?orderId=         #634 — the trips recorded for one order (tripId = leg id)
 *   DELETE /{id}/trips/{tripId}          204, trip returns to the pool (422 on the last one)
 *   POST   /{id}/void?reason=            the updated settlement
 *   POST   /{id}/pay                     multipart amount [+ reference, invoiceFile, providerVatRegistered, paymentDate]
 *
 * Every signature here is pinned to `AdminProviderSettlementController` — the reason `void` takes
 * its reason on the query string and the payment part is `proofFile` (not the garage payout's
 * `confirmationFile`) is that the controller says so, and a test asserts each one.
 */

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

/**
 * Multipart upload. `api.ts` sets `Content-Type: application/json` on every request, which would
 * strip the boundary, so multipart goes through `fetch` directly — the same carve-out
 * `settlementsApi.uploadMultipart` and `financeApi` already make.
 */
async function uploadMultipart<T>(url: string, formData: FormData, token: string): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    // GlobalExceptionHandler bodies are { error, message, status }; the human sentence
    // ("Enter the invoiced amount", "Trip already settled") is in `message`.
    const err = await response.json().catch(() => ({}));
    throw new ApiError(err.message || err.error || `HTTP ${response.status}`, response.status);
  }
  return response.json() as Promise<T>;
}

export interface ProviderSettlementFilters {
  providerId?: string;
  status?: ProviderSettlementStatus | '';
}

/**
 * The list may arrive as a bare array or as a Spring page (`{content}`) depending on how
 * merchant-settlement renders it — `AdminSettlementController` pages its list while the newer
 * finance endpoints answer arrays. Normalising here keeps one shape in the components and means a
 * page wrapper on the wire is a non-event rather than a blank tab.
 */
export function unwrapList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const content = (payload as { content?: unknown }).content;
    if (Array.isArray(content)) return content as T[];
    const items = (payload as { items?: unknown }).items;
    if (Array.isArray(items)) return items as T[];
  }
  return [];
}

export const providerSettlementsApi = {
  list: async (filters: ProviderSettlementFilters, token: string) => {
    const query = new URLSearchParams();
    if (filters.providerId) query.set('providerId', filters.providerId);
    if (filters.status) query.set('status', filters.status);
    const qs = query.toString();
    const payload = await api.get<unknown>(`/api/admin/provider-settlements${qs ? `?${qs}` : ''}`, token);
    return unwrapList<ProviderSettlement>(payload);
  },

  get: (id: string, token: string) =>
    api.get<ProviderSettlementDetail>(`/api/admin/provider-settlements/${encodeURIComponent(id)}`, token),

  /**
   * #634 — every provider with unsettled trips and how many, read from the trips rather than the
   * orders registry, so an inactive or retired provider that is still owed stays pickable.
   */
  unsettledProviders: (token: string) =>
    api.get<UnsettledProvider[]>('/api/admin/provider-settlements/unsettled-providers', token),

  /**
   * #634 — the provider's completed trips on no settlement yet (the pool Prepare would collect).
   * Same screen and gateway route as the rest of this file; `providerId` is required (400 without).
   */
  unsettledTrips: (providerId: string, token: string, period: TripPeriod = {}) => {
    const query = new URLSearchParams({ providerId });
    if (period.from) query.set('from', period.from);
    if (period.to) query.set('to', period.to);
    return api.get<UnsettledProviderTrips>(`/api/admin/provider-settlements/unsettled-trips?${query}`, token);
  },

  /**
   * Marks the provider's unsettled trips in the period as paid, in one step (201). Only the amount
   * is mandatory; the invoice can be attached later. 409 when the period has nothing to pay or the
   * trip count no longer matches what the admin was shown.
   */
  payPeriod: (input: PayPeriodInput, token: string) => {
    const form = new FormData();
    form.append('providerId', input.providerId);
    if (input.from) form.append('from', input.from);
    form.append('to', input.to);
    form.append('tripCount', String(input.tripCount));
    form.append('amount', String(input.amount));
    form.append('providerVatRegistered', String(input.providerVatRegistered));
    if (input.providerTrn) form.append('providerTrn', input.providerTrn);
    if (input.paymentDate) form.append('paymentDate', input.paymentDate);
    if (input.reference) form.append('reference', input.reference);
    if (input.invoice) form.append('invoiceFile', input.invoice);
    return uploadMultipart<ProviderSettlementDetail>('/api/admin/provider-settlements/pay-period', form, token);
  },

  /** Attaches the provider's invoice to a settlement paid without one. 409 if one is already attached. */
  attachInvoice: (id: string, invoice: File, token: string) => {
    const form = new FormData();
    form.append('invoiceFile', invoice);
    return uploadMultipart<ProviderSettlementDetail>(
      `/api/admin/provider-settlements/${encodeURIComponent(id)}/invoice`,
      form,
      token,
    );
  },

  /**
   * #634 — the trips merchant-settlement recorded for one order, one per completed Garagat leg that
   * had a provider (`tripId` is the orders leg id). Empty = no trip recorded. Same screen
   * (`settlements`) and gateway route as the rest of this file.
   */
  orderTrips: (orderId: string, token: string) =>
    api.get<ProviderTrip[]>(
      `/api/admin/provider-settlements/order-trips?orderId=${encodeURIComponent(orderId)}`,
      token,
    ),

  /**
   * Collects every completed, unsettled trip for the provider into a DRAFT.
   * 409 "Nothing to settle for this provider" when the pool is empty (AC-42) — an expected answer,
   * not a failure, so the caller renders it inline beside the button.
   */
  prepare: (providerId: string, token: string) =>
    api.post<ProviderSettlementDetail>(
      `/api/admin/provider-settlements/prepare?providerId=${encodeURIComponent(providerId)}`,
      undefined,
      token,
    ),

  /** Returns one trip to the pool. 204; the caller refetches the detail for the new total. */
  excludeTrip: (id: string, tripId: string, token: string) =>
    api.delete<void>(
      `/api/admin/provider-settlements/${encodeURIComponent(id)}/trips/${encodeURIComponent(tripId)}`,
      token,
    ),

  /**
   * Voids the settlement; every trip returns to the pool and can be collected again (AC-42).
   * The reason is a **query parameter**, not a body — `@RequestParam String reason`.
   */
  void: (id: string, reason: string, token: string) =>
    api.post<ProviderSettlement>(
      `/api/admin/provider-settlements/${encodeURIComponent(id)}/void?reason=${encodeURIComponent(reason)}`,
      undefined,
      token,
    ),

  /**
   * Pays an existing DRAFT. Only the amount is mandatory since 2026-09-27; 400 "Enter the invoiced
   * amount" when it is missing.
   */
  pay: (id: string, input: RecordProviderPaymentInput, token: string) => {
    const form = new FormData();
    form.append('amount', String(input.amount));
    if (input.reference) form.append('reference', input.reference);
    if (input.invoice) form.append('invoiceFile', input.invoice);
    if (input.providerVatRegistered !== undefined) {
      form.append('providerVatRegistered', String(input.providerVatRegistered));
    }
    if (input.paymentDate) form.append('paymentDate', input.paymentDate);
    return uploadMultipart<ProviderSettlementDetail>(
      `/api/admin/provider-settlements/${encodeURIComponent(id)}/pay`,
      form,
      token,
    );
  },
};

/**
 * The sentence to show for a rejected action, and where it belongs.
 *
 * Flow F7's two expected rejections are a 409 ("Nothing to settle for this provider",
 * "Trip already settled") and a 400/422 validation failure ("Enter the invoiced amount"). Both are
 * information the admin acts on, so they are rendered inline next to the control that produced
 * them. Anything else — 500, a dropped connection — is a failure, not an answer, and says so.
 */
export function actionMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** True for the answers that are part of the flow rather than a breakage (409 / 400 / 422). */
export function isExpectedRejection(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 409 || err.status === 400 || err.status === 422);
}
